import React, { useState, useEffect, useRef } from 'react';
import { Facebook, Instagram, Clock, Users, ExternalLink, Play, Pause, Sparkles, Image, MapPin, PieChart, DollarSign } from 'lucide-react';

/**
 * AdCard - Displays a single competitor ad
 *
 * Features:
 * - Lazy-loaded iframe for ad_snapshot_url (with timeout detection)
 * - Platform badges (Facebook, Instagram)
 * - Active/stopped status
 * - Longevity and reach metrics
 * - Primary ad body text
 *
 * Note: Meta's ad_snapshot_url may not render in iframes on external domains
 * due to X-Frame-Options restrictions. In this case, a placeholder is shown.
 */
const AdCard = ({ ad, onClick }) => {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeError, setIframeError] = useState(false);
  const [iframeTimeout, setIframeTimeout] = useState(false);
  const iframeRef = useRef(null);

  // Detect if iframe content actually rendered (timeout-based detection)
  useEffect(() => {
    if (!ad.adSnapshotUrl) return;

    const timer = setTimeout(() => {
      // If iframe didn't report loaded after 5s, assume it's blocked
      if (!iframeLoaded && !iframeError) {
        setIframeTimeout(true);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [ad.adSnapshotUrl, iframeLoaded, iframeError]);

  // Get platform icon
  const getPlatformIcon = (platform) => {
    switch (platform?.toLowerCase()) {
      case 'facebook':
        return <Facebook size={14} />;
      case 'instagram':
        return <Instagram size={14} />;
      default:
        return null;
    }
  };

  // Format reach number
  const formatReach = (reach) => {
    if (!reach) return null;
    if (reach >= 1000000) {
      return `${(reach / 1000000).toFixed(1)}M`;
    }
    if (reach >= 1000) {
      return `${(reach / 1000).toFixed(0)}K`;
    }
    return reach.toString();
  };

  // Get primary body text
  const primaryBody = ad.bodies?.[0] || '';
  const truncatedBody = primaryBody.length > 120
    ? primaryBody.substring(0, 120) + '...'
    : primaryBody;

  // Get primary title
  const primaryTitle = ad.linkTitles?.[0] || '';

  // Format demographic distribution for display
  const getTopDemographic = () => {
    if (!ad.demographicDistribution || !Array.isArray(ad.demographicDistribution)) return null;
    // Sort by percentage and get top segment
    const sorted = [...ad.demographicDistribution].sort((a, b) => (b.percentage || 0) - (a.percentage || 0));
    if (sorted.length === 0) return null;
    const top = sorted[0];
    return `${top.age || '?'} ${top.gender === 'female' ? '♀' : top.gender === 'male' ? '♂' : ''} ${Math.round((top.percentage || 0) * 100)}%`;
  };

  // Format region distribution for display
  const getTopRegion = () => {
    if (!ad.deliveryByRegion || !Array.isArray(ad.deliveryByRegion)) return null;
    const sorted = [...ad.deliveryByRegion].sort((a, b) => (b.percentage || 0) - (a.percentage || 0));
    if (sorted.length === 0) return null;
    const top = sorted[0];
    return `${top.region || '?'} ${Math.round((top.percentage || 0) * 100)}%`;
  };

  // Format spend for display
  const formatSpend = () => {
    if (!ad.spend) return null;
    const { lower_bound, upper_bound, currency } = ad.spend;
    if (!lower_bound && !upper_bound) return null;
    const curr = currency || 'EUR';
    return `${lower_bound || '?'}-${upper_bound || '?'} ${curr}`;
  };

  const topDemographic = getTopDemographic();
  const topRegion = getTopRegion();
  const spendRange = formatSpend();

  return (
    <div
      style={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        cursor: 'pointer'
      }}
      onClick={() => onClick?.(ad)}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'none';
      }}
    >
      {/* Ad Preview (image, iframe, or placeholder) */}
      <div style={{
        position: 'relative',
        width: '100%',
        height: '200px',
        backgroundColor: '#f8fafc',
        overflow: 'hidden'
      }}>
        {/* Priority 1: Use linkImages if available */}
        {ad.linkImages && ad.linkImages.length > 0 ? (
          <img
            src={ad.linkImages[0]}
            alt={ad.linkTitles?.[0] || 'Ad creative'}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            onError={(e) => {
              // If image fails, hide it and show placeholder
              e.target.style.display = 'none';
              setIframeError(true);
            }}
          />
        ) : (iframeError || iframeTimeout || !ad.adSnapshotUrl) ? (
          /* Priority 2: Show placeholder when no image and iframe fails */
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
            color: '#0369a1',
            gap: '8px'
          }}>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '12px',
              backgroundColor: 'rgba(3, 105, 161, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Image size={28} />
            </div>
            <span style={{ fontSize: '12px', fontWeight: '500' }}>
              Cliquez pour voir la pub
            </span>
            <span style={{ fontSize: '10px', color: '#64748b' }}>
              Ouverture dans Meta Ad Library
            </span>
          </div>
        ) : (
          /* Priority 3: Try iframe (usually blocked by Meta) */
          <>
            {/* Loading spinner */}
            {!iframeLoaded && (
              <div style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f3f4f6'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  border: '3px solid #e5e7eb',
                  borderTopColor: '#3b82f6',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
              </div>
            )}
            <iframe
              ref={iframeRef}
              src={ad.adSnapshotUrl}
              title={`Ad ${ad.externalAdId}`}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                opacity: iframeLoaded ? 1 : 0,
                transition: 'opacity 0.3s ease'
              }}
              loading="lazy"
              sandbox="allow-scripts allow-same-origin"
              onLoad={() => setIframeLoaded(true)}
              onError={() => setIframeError(true)}
            />
          </>
        )}

        {/* Status badge */}
        <div style={{
          position: 'absolute',
          top: '8px',
          left: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '4px 8px',
          borderRadius: '6px',
          fontSize: '11px',
          fontWeight: '600',
          backgroundColor: ad.isActive ? 'rgba(34, 197, 94, 0.9)' : 'rgba(239, 68, 68, 0.9)',
          color: '#fff'
        }}>
          {ad.isActive ? <Play size={12} /> : <Pause size={12} />}
          {ad.isActive ? 'Active' : 'Stoppée'}
        </div>

        {/* New badge */}
        {ad.isNew && (
          <div style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '4px 8px',
            borderRadius: '6px',
            fontSize: '11px',
            fontWeight: '600',
            backgroundColor: 'rgba(245, 158, 11, 0.9)',
            color: '#fff'
          }}>
            <Sparkles size={12} />
            Nouveau
          </div>
        )}
      </div>

      {/* Content */}
      <div style={{ padding: '12px' }}>
        {/* Page name and platforms */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '8px'
        }}>
          <span style={{
            fontSize: '12px',
            color: '#6b7280',
            fontWeight: '500'
          }}>
            {ad.page?.label || ad.pageNameAtPublish || 'Page inconnue'}
          </span>

          <div style={{ display: 'flex', gap: '4px' }}>
            {(ad.publisherPlatforms || []).map((platform, idx) => (
              <span
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '24px',
                  height: '24px',
                  borderRadius: '6px',
                  backgroundColor: platform === 'facebook' ? '#1877f2' : '#E4405F',
                  color: '#fff'
                }}
                title={platform}
              >
                {getPlatformIcon(platform)}
              </span>
            ))}
          </div>
        </div>

        {/* Title */}
        {primaryTitle && (
          <h4 style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#111827',
            marginBottom: '6px',
            lineHeight: '1.3'
          }}>
            {primaryTitle}
          </h4>
        )}

        {/* Body text */}
        {truncatedBody && (
          <p style={{
            fontSize: '13px',
            color: '#4b5563',
            lineHeight: '1.4',
            marginBottom: '12px'
          }}>
            {truncatedBody}
          </p>
        )}

        {/* Metrics */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          paddingTop: '8px',
          borderTop: '1px solid #f3f4f6'
        }}>
          {/* Longevity */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '12px',
            color: '#6b7280'
          }}>
            <Clock size={14} />
            <span>{ad.longevityDays || 0}j</span>
          </div>

          {/* Reach */}
          {ad.euTotalReach && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '12px',
              color: '#6b7280'
            }}>
              <Users size={14} />
              <span>{formatReach(ad.euTotalReach)}</span>
            </div>
          )}

          {/* Languages */}
          {ad.languages?.length > 0 && (
            <span style={{
              fontSize: '11px',
              color: '#9ca3af',
              marginLeft: 'auto'
            }}>
              {ad.languages.slice(0, 2).join(', ')}
            </span>
          )}
        </div>

        {/* Additional insights (demographic, region, spend) */}
        {(topDemographic || topRegion || spendRange) && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            paddingTop: '8px',
            marginTop: '8px',
            borderTop: '1px solid #f3f4f6',
            flexWrap: 'wrap'
          }}>
            {/* Top demographic */}
            {topDemographic && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '11px',
                color: '#8b5cf6',
                backgroundColor: '#f5f3ff',
                padding: '2px 6px',
                borderRadius: '4px'
              }}
              title="Segment démographique principal"
              >
                <PieChart size={12} />
                <span>{topDemographic}</span>
              </div>
            )}

            {/* Top region */}
            {topRegion && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '11px',
                color: '#0891b2',
                backgroundColor: '#ecfeff',
                padding: '2px 6px',
                borderRadius: '4px'
              }}
              title="Région principale"
              >
                <MapPin size={12} />
                <span>{topRegion}</span>
              </div>
            )}

            {/* Spend range */}
            {spendRange && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '11px',
                color: '#059669',
                backgroundColor: '#ecfdf5',
                padding: '2px 6px',
                borderRadius: '4px'
              }}
              title="Fourchette de dépenses"
              >
                <DollarSign size={12} />
                <span>{spendRange}</span>
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AdCard;
